import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { z } from "zod";
import bridge from "@vkontakte/vk-bridge";
import {
  shopOrderInputSchema,
  type ShopOrderView,
  type ShopView,
} from "@frontier/content/shop";
import type { Profile } from "@frontier/protocol/contracts";
import type { GamePlatform } from "./platform-launch";
import type { ProfileSync } from "./profile-sync";
import { browserAuthStorage } from "./auth-storage";
import { api, ApiError, requestId } from "./session-api";
import { shopOrderMessage } from "./shop-order-status";
import { shopServerTime } from "./shop-clock";
const draftSchema = z.strictObject({
  input: shopOrderInputSchema,
  id: z.uuid().optional(),
});
type Draft = z.infer<typeof draftSchema>;
export function useShopActions(
  profile: Profile | null,
  platform: GamePlatform,
  token: RefObject<string>,
  sync: ProfileSync,
  setNotice: (value: string) => void,
) {
  const [storage] = useState(browserAuthStorage);
  const [catalog, setCatalog] = useState<{
    view: ShopView;
    receivedAt: number;
    playerId: string;
    ticket: number;
  } | null>(null);
  const currentCatalog = useRef<typeof catalog>(null);
  const catalogRead = useRef(0);
  const expiredRefresh = useRef(new Set<string>());
  const [draft, setDraft] = useState<Draft | null>(null);
  const currentDraft = useRef<Draft | null>(null);
  const [order, setOrder] = useState<ShopOrderView | null>(null);
  const [busy, setBusy] = useState(false);
  const paying = useRef(false);
  const checkoutAttempt = useRef(requestId());
  const active = useRef(profile);
  active.current = profile;
  const key = (id: string) => `frontier.currencyOrder.${id}`;
  const saveDraft = useCallback(
    (playerId: string, next: Draft | null) => {
      if (next) storage.setItem(key(playerId), JSON.stringify(next));
      else storage.removeItem(key(playerId));
      currentDraft.current = next;
      setDraft(next);
    },
    [storage],
  );
  const ownsDraft = useCallback(
    (playerId: string, intent: Draft, ticket: number) =>
      ticket === sync.ticket() &&
      active.current?.playerId === playerId &&
      currentDraft.current?.input.requestId === intent.input.requestId,
    [sync],
  );
  const reload = useCallback(async () => {
    const player = active.current;
    if (!player || !token.current) return;
    const ticket = sync.ticket();
    const sequence = ++catalogRead.current;
    try {
      const view = await api.shop(token.current);
      if (
        ticket === sync.ticket() &&
        active.current?.playerId === player.playerId &&
        sequence === catalogRead.current
      ) {
        const next = {
          view,
          receivedAt: performance.now(),
          playerId: player.playerId,
          ticket,
        };
        currentCatalog.current = next;
        setCatalog(next);
      }
    } catch (error) {
      if (
        ticket === sync.ticket() &&
        active.current?.playerId === player.playerId &&
        sequence === catalogRead.current
      ) {
        const message =
          error instanceof Error
            ? error.message
            : "Не удалось получить магазин.";
        console.warn("shop-catalog-read-failed", {
          playerId: player.playerId,
          message,
        });
        setNotice(message);
      }
    }
  }, [token, sync, setNotice]);
  const refreshExpired = useCallback(async () => {
    const snapshot = currentCatalog.current;
    if (
      !snapshot ||
      snapshot.playerId !== active.current?.playerId ||
      snapshot.ticket !== sync.ticket()
    )
      return;
    const now = shopServerTime(
      snapshot.view.serverNow,
      snapshot.receivedAt,
      performance.now(),
    );
    const expired = snapshot.view.items.filter(
      (item) => item.endsAt !== null && item.endsAt <= now,
    );
    let refresh = false;
    for (const item of expired) {
      const identity =
        item.offerId ?? `${item.sku}:${item.version}:${item.endsAt}`;
      if (!expiredRefresh.current.has(identity)) {
        expiredRefresh.current.add(identity);
        refresh = true;
      }
    }
    if (refresh) await reload();
  }, [reload, sync]);
  useEffect(() => {
    setCatalog(null);
    setOrder(null);
    setDraft(null);
    currentDraft.current = null;
    setBusy(false);
    paying.current = false;
    checkoutAttempt.current = requestId();
    currentCatalog.current = null;
    catalogRead.current++;
    expiredRefresh.current.clear();
    if (!profile) return;
    try {
      const saved = storage.getItem(key(profile.playerId));
      const parsed = draftSchema.safeParse(saved ? JSON.parse(saved) : null);
      if (parsed.success) {
        currentDraft.current = parsed.data;
        setDraft(parsed.data);
      }
    } catch {
      /* A local draft never substitutes for a server order. */
    }
  }, [profile?.playerId, storage]);
  const finishOrder = useCallback(async (
    next: ShopOrderView, playerId: string, intent: Draft, ticket: number,
  ) => {
    if (!ownsDraft(playerId, intent, ticket)) return;
    setOrder(next);
    if (next.state === "created") {
      setNotice(shopOrderMessage(next));
      return;
    }
    const refreshed = await sync.refresh(ticket);
    if (!ownsDraft(playerId, intent, ticket)) return;
    if (!refreshed) {
      setNotice("Статус заказа подтверждён. Обновляем баланс…");
      return;
    }
    saveDraft(playerId, null);
    setNotice(shopOrderMessage(next));
    await reload();
  }, [ownsDraft, sync, saveDraft, setNotice, reload]);
  const checkOrder = useCallback(async () => {
    const player = active.current;
    const intent = currentDraft.current;
    if (!player || !intent?.id) return;
    const ticket = sync.ticket();
    try {
      const next = await api.shopOrder(token.current, intent.id);
      if (!ownsDraft(player.playerId, intent, ticket)) return;
      await finishOrder(next, player.playerId, intent, ticket);
    } catch (error) {
      if (ownsDraft(player.playerId, intent, ticket))
        setNotice(
          error instanceof Error
            ? error.message
            : "Подтверждение пока недоступно.",
        );
    }
  }, [token, sync, ownsDraft, finishOrder, setNotice]);
  useEffect(() => {
    if (!draft?.id) return;
    void checkOrder();
    const timer = window.setInterval(() => void checkOrder(), 5000);
    return () => window.clearInterval(timer);
  }, [draft?.id, checkOrder]);
  const pay = useCallback(
    async (sku?: z.infer<typeof shopOrderInputSchema>["sku"]) => {
      const player = active.current;
      if (!player || platform.kind !== "vkgames" || busy || paying.current)
        return;
      const ticket = sync.ticket();
      let intent = currentDraft.current;
      if (!intent) {
        const snapshot = currentCatalog.current;
        const item = snapshot?.view.items.find((item) => item.sku === sku);
        if (
          !snapshot ||
          snapshot.playerId !== player.playerId ||
          snapshot.ticket !== ticket ||
          !item ||
          !snapshot.view.enabled ||
          !snapshot.view.eligible ||
          !item.available
        )
          return;
        const now = shopServerTime(
          snapshot.view.serverNow,
          snapshot.receivedAt,
          performance.now(),
        );
        if (
          now < item.startsAt ||
          (item.endsAt !== null && now >= item.endsAt)
        ) {
          setNotice("Предложение завершилось. Выбери новый набор.");
          await refreshExpired();
          return;
        }
        intent = {
          input: {
            requestId: requestId(),
            sku: item.sku,
            version: item.version,
          },
        };
        saveDraft(player.playerId, intent);
      }
      paying.current = true;
      setBusy(true);
      let created = !!intent.id;
      try {
        const next = await api.createShopOrder(token.current, intent.input);
        if (!ownsDraft(player.playerId, intent, ticket)) return;
        created = true;
        const saved: Draft = { input: intent.input, id: next.id };
        saveDraft(player.playerId, saved);
        setOrder(next);
        if (next.state !== "created") {
          await finishOrder(next, player.playerId, saved, ticket);
          return;
        }
        if (next.paymentStarted) {
          setNotice("Оплата не завершена. Проверь или отмени заказ.");
          return;
        }
        if (!(await bridge.supportsAsync("VKWebAppShowOrderBox")))
          throw new Error("В этой версии VK окно оплаты недоступно.");
        if (!ownsDraft(player.playerId, intent, ticket)) return;
        await api.startShopCheckout(
          token.current,
          next.id,
          checkoutAttempt.current,
        );
        if (!ownsDraft(player.playerId, intent, ticket)) return;
        const result = await bridge.send("VKWebAppShowOrderBox", {
          type: "item",
          item: next.item,
        });
        if (!ownsDraft(player.playerId, intent, ticket)) return;
        // The Bridge result only closes the UI; the signed callback is the grant authority.
        const confirmed =
          result.status === "cancel"
            ? await api.closeShopOrder(token.current, next.id)
            : await api.shopOrder(token.current, next.id);
        if (!ownsDraft(player.playerId, intent, ticket)) return;
        await finishOrder(confirmed, player.playerId, intent, ticket);
      } catch (error) {
        if (!ownsDraft(player.playerId, intent, ticket)) return;
        const rejected =
          error instanceof ApiError &&
          error.status >= 400 &&
          error.status < 500 &&
          ![401, 408, 426, 429].includes(error.status) &&
          error.code !== "CLIENT_UPDATE_REQUIRED";
        if (rejected && !created) saveDraft(player.playerId, null);
        if (rejected && !created && error.code === "SHOP_CHANGED") {
          setNotice("Предложение изменилось. Выбери новый набор.");
          await reload();
        } else
          setNotice(
            error instanceof Error
              ? error.message
              : "Проверь незавершённый заказ перед новой покупкой.",
          );
      } finally {
        if (ticket === sync.ticket()) {
          paying.current = false;
          setBusy(false);
        }
      }
    },
    [
      platform,
      busy,
      ownsDraft,
      saveDraft,
      token,
      sync,
      reload,
      refreshExpired,
      finishOrder,
      setNotice,
    ],
  );
  const closeOrder = useCallback(async () => {
    const player = active.current;
    const intent = currentDraft.current;
    if (!player || !intent?.id || busy) return;
    const ticket = sync.ticket();
    setBusy(true);
    try {
      const next = await api.closeShopOrder(token.current, intent.id);
      if (!ownsDraft(player.playerId, intent, ticket)) return;
      await finishOrder(next, player.playerId, intent, ticket);
    } catch (error) {
      if (ownsDraft(player.playerId, intent, ticket))
        setNotice(
          error instanceof Error
            ? error.message
            : "Пока не удалось закрыть заказ.",
        );
    } finally {
      if (ticket === sync.ticket()) setBusy(false);
    }
  }, [busy, token, sync, ownsDraft, finishOrder, setNotice]);
  return {
    shop: catalog?.view ?? null,
    shopReceivedAt: catalog?.receivedAt ?? null,
    shopOrder: order,
    pendingShop: draft,
    shopBusy: busy,
    reloadShop: reload,
    refreshExpiredShop: refreshExpired,
    buyCurrency: pay,
    checkShopOrder: checkOrder,
    closeShopOrder: closeOrder,
  };
}
